import moment from 'moment';
import {isDate, isNumber, isString, reduce, filter} from 'lodash';
import {isEmptyVal} from "@/utils/index";
import { BILLING_CYCLE, NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP } from '@/common/constants';

export const LocalDateFormat = 'LL';
export const LocalDateTimeFormat = 'LL LT';
export const LocalDateTimeSecondFormat = 'LL LTS';

/**
 * convert to moment object
 * @param {string|number|date|moment.Moment} value
 * @returns {moment.Moment|null}
 */
const convertToMoment = (value) => {
  if (moment.isMoment(value)) {
    return value;
  }
  if ((isDate(value) || isNumber(value) || isString(value)) && moment(value).isValid()) {
    return moment(value);
  }
  return null;
}

/**
 * get date format result
 * @param {string|number|date|moment.Moment} value
 * @param {(v: moment.Moment) => string} formatFunc
 * @returns
 */
const getFormatResult = (value, formatFunc) => {
  if (isEmptyVal(value)) {
    return '';
  }
  const momentValue = convertToMoment(value);
  return momentValue ? formatFunc(momentValue) : value;
}

export const dateTimeFormat = (value, formatDateTime = LocalDateFormat) => {
  // This method directly parses the provided date string into UTC time and creates a corresponding Moment object.
  // This means that the Moment object will always maintain the UTC time zone in subsequent operations,
  // no matter what operations are performed afterwards.
  return getFormatResult(value, (momentValue) =>
    momentValue.utc().format(formatDateTime)
  );
};

export const dateTimeFormatWithClientZone = (value, formatDateTime = LocalDateFormat) => {
  return getFormatResult(value, (momentValue) =>
    momentValue.format(formatDateTime)
  );
}

/**
 * value
 * @param value
 * @param options default utc zone
 * @return {string|*}
 */
export const dateTimeFormatWithUtcOffset = (value, options) => {
  const { formatDateTime = LocalDateFormat, offset = 0 } = options || {};
  return getFormatResult(value, (momentValue) =>
    momentValue.utcOffset(offset).format(formatDateTime)
  );
};

export const addDaysWithFormatDate = (value, options) => {
  // offset default -8, means to convert to PST zone
  const { period = 1, unit = "day", offset = -8, formatDateTime = LocalDateFormat } = options;
  return getFormatResult(value, (momentValue) =>
    momentValue.utcOffset(offset).add(period, unit).format(formatDateTime)
  );
}

export const currentPstDate = dateTimeFormatWithUtcOffset(Date.now(), {offset: -8});

// next annual/monthly pst date
export const getNextPstCycleDate = (options = {}) => {
  const { offset = -8, formatDateTime = 'YYYY-MM-DD' } = options;

  return {
    [NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[BILLING_CYCLE.ANNUAL]]: addDaysWithFormatDate(Date.now(), {
      offset,
      period: 1,
      unit: 'year',
      formatDateTime,
    }),
    [NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[BILLING_CYCLE.MONTHLY]]: addDaysWithFormatDate(Date.now(), {
      offset,
      period: 1,
      unit: 'month',
      formatDateTime,
    }),
    [NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[BILLING_CYCLE.QUARTERLY]]: addDaysWithFormatDate(Date.now(), {
      offset,
      period: 1,
      unit: 'quarter',
      formatDateTime,
    }),
    [NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[BILLING_CYCLE.TWO_YEARS]]: addDaysWithFormatDate(Date.now(), {
      offset,
      period: 2,
      unit: 'year',
      formatDateTime,
    }),
    [NEXT_CYCLE_KEY_WITH_BILLING_CYCLE_MAP[BILLING_CYCLE.THREE_YEARS]]: addDaysWithFormatDate(Date.now(), {
      offset,
      period: 3,
      unit: 'year',
      formatDateTime,
    }),
  };
};


/**
 *
 * @param source
 * @param target
 * @return {boolean}
 */
export const compareDate = (source, target) => {
  if (moment(source).isValid() && moment(target).isValid()) {
    return moment(source) > moment(target);
  }

  return false;
}

/**
 *
 * @param discountPlans {expireDate: string} eg: {expireDate: '2022-11-20'}
 * @return {string}
 */
export const getEarliestTime = (discountPlans = []) => {
  const filterDiscountPlans = filter(discountPlans, (item) => !!item?.expireDate);

  return reduce(filterDiscountPlans, (pre, next) => {
    const { expireDate } = next || {};

    if (isEmptyVal(pre) || compareDate(pre, expireDate)) {
      pre = expireDate;
    }

    return pre;
  }, '')
}
